import { EntityType } from '@frontend/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { CommonMessage } from '@frontend/lang';
import { OrderExtendedClient } from '@frontend/order/api';
import { OrderStatusBadge } from '@frontend/order/badge';
import { ExtendedOrder } from '@frontend/order/types';
import { useAccountRepository } from '@frontend/repository';

import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { useAppDispatch } from '../../../redux/store';
import OverviewHeader from '../../header/overview-header';

const ID = 'shop-orders';

export const ShopOrders = () => {
    const { currentAccount } = useAccountRepository();
    const dispatch = useAppDispatch();
    const { openPlatformDetail } = usePlatformModalHost();

    return (
        <div
            id={ID}
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader title={CommonMessage.OBJECTS.ORDER.PLURAL} />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <ReactiveObjectList<ExtendedOrder>
                    id='shop-orders-list'
                    useStaticListId
                    showCount
                    dispatch={dispatch}
                    fetch={(arg) =>
                        OrderExtendedClient.fetchOrdersExtended({
                            ...arg,
                            account_id: currentAccount ?? undefined
                        })
                    }
                    type={EntityType.ORDER}
                    eventListener={EntityType.ORDER}
                    onSelectElement={(order) =>
                        openPlatformDetail({
                            entityType: EntityType.ORDER,
                            id: order.id
                        })
                    }
                    sortingColumns={['number', 'creation_timestamp', 'state']}
                    activeFilter={{
                        searchKeys: ['number', 'user'],
                        filterKeys: ['after', 'before'],
                        displayName: new Map([
                            ['number', CommonMessage.OBJECTS.ORDER.ORDER_NUMBER],
                            ['user', CommonMessage.OBJECTS.USER.DEFAULT],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                            ['after', CommonMessage.FORMS.FROM_TIMESTAMP],
                            ['before', CommonMessage.FORMS.TO_TIMESTAMP]
                        ])
                    }}
                    columns={{
                        columns: ['number', 'user_id', 'content', 'state', 'creation_timestamp'],
                        headerOverwrite: new Map([
                            ['number', CommonMessage.OBJECTS.ORDER.ORDER_NUMBER],
                            ['user_id', CommonMessage.OBJECTS.USER.DEFAULT],
                            ['content', CommonMessage.OBJECTS.TRANSACTION.CONTENT],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                        ]),
                        customCells: new Map([
                            [
                                'user_id',
                                (obj) => {
                                    const order = obj.row.original;
                                    return (
                                        <td
                                            key={order.user_id ?? order.id}
                                            className={defaultCellClassName}>
                                            {order.user_id ? (
                                                <PlatformObjectBadge
                                                    id={order.user_id}
                                                    entityType={EntityType.USER}
                                                    display={order.user?.name}
                                                />
                                            ) : (
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            )}
                                        </td>
                                    );
                                }
                            ],
                            [
                                'content',
                                (obj) => {
                                    const order = obj.row.original;
                                    return (
                                        <td
                                            key={`${order.id}-content`}
                                            className={defaultCellClassName}>
                                            <div className='text-foreground-default inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-sm'>
                                                <span>
                                                    {order.quantity}x {order.product?.name ?? order.product_id}
                                                </span>
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'state',
                                (obj) => (
                                    <td
                                        key={obj.row.original.id}
                                        className={defaultCellClassName}>
                                        <OrderStatusBadge status={obj.row.original.state} />
                                    </td>
                                )
                            ]
                        ])
                    }}
                />
            </div>
        </div>
    );
};
